/**
 * PDF Exporter Utility
 * Handles PDF generation from resume HTML
 * Uses html2pdf.js library for client-side PDF generation
 */

import html2pdf from 'html2pdf.js';

/**
 * Export an iframe's content to PDF
 * @param {HTMLIFrameElement} iframeRef - Reference to the iframe containing the resume
 * @param {string} filename - Name for the PDF file
 * @param {object} options - Additional options for PDF generation
 * @returns {Promise<void>}
 */
export const exportIframeToPDF = async (iframeRef, filename = 'resume.pdf', options = {}) => {
  try {
    if (!iframeRef || !iframeRef.contentDocument) {
      throw new Error('Invalid iframe reference');
    }

    console.log('📄 Starting PDF export...');

    // Get the iframe's document
    const iframeDoc = iframeRef.contentDocument || iframeRef.contentWindow.document;
    const element = iframeDoc.body;

    if (!element) {
      throw new Error('No content found in iframe');
    }

    // 🔍 DEBUG: Check iframe content
    console.log('🔍 Iframe readyState:', iframeDoc.readyState);
    console.log('🔍 Iframe body exists:', !!element);
    console.log('🔍 Iframe body innerHTML length:', element.innerHTML?.length || 0);
    console.log('🔍 Iframe body children count:', element.children?.length || 0);
    
    if (!element.innerHTML || element.innerHTML.trim().length === 0) {
      console.error('❌ Iframe body is empty!');
      throw new Error('Iframe content is empty. Please ensure preview is loaded before downloading.');
    }

    // Default PDF options
    const defaultOptions = {
      margin: [10, 10, 10, 10], // top, right, bottom, left in mm
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait'
      },
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.page-break-avoid'
      }
    };

    // Merge with custom options
    const pdfOptions = { ...defaultOptions, ...options };

    // 🔧 CRITICAL FIX: Export iframe body directly to preserve all styles
    console.log('🔧 Attempting direct export from iframe body with styles...');
    
    try {
      // Create a wrapper in parent document that will host the cloned iframe content
      const exportWrapper = document.createElement('div');
      exportWrapper.style.position = 'absolute';
      exportWrapper.style.left = '-99999px';
      exportWrapper.style.top = '0';
      exportWrapper.style.width = '210mm';
      exportWrapper.style.background = 'white';
      document.body.appendChild(exportWrapper);
      
      // Clone iframe body
      const clonedBody = element.cloneNode(true);
      
      // Copy all stylesheets from iframe to a style tag
      const allStyles = [];
      const styleElements = iframeDoc.querySelectorAll('style');
      styleElements.forEach(styleEl => {
        allStyles.push(styleEl.textContent);
      });
      
      // Create combined style element
      const combinedStyle = document.createElement('style');
      combinedStyle.textContent = allStyles.join('\n');
      exportWrapper.appendChild(combinedStyle);
      exportWrapper.appendChild(clonedBody);
      
      // 🔧 NOW check for hidden elements in the cloned body (after it's in DOM)
      const allElements = clonedBody.querySelectorAll('*');
      let hiddenCount = 0;
      allElements.forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden' || parseFloat(computedStyle.opacity) < 0.1) {
          hiddenCount++;
          el.style.display = 'block';
          el.style.visibility = 'visible';
          el.style.opacity = '1';
        }
      });
      
      console.log('🔧 Fixed', hiddenCount, 'hidden elements');
      console.log('📊 After visibility fix - text length:', (clonedBody.innerText || clonedBody.textContent || '').length);
      
      console.log('📊 Exporting with combined styles...');
      console.log('  - Styles length:', allStyles.join('\n').length);
      console.log('  - Body HTML length:', clonedBody.innerHTML.length);
      
      // 🔧 CRITICAL: Give browser time to apply styles and render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('🔍 Final wrapper dimensions:', exportWrapper.offsetWidth, 'x', exportWrapper.offsetHeight);
      console.log('🔍 Final wrapper scrollHeight:', exportWrapper.scrollHeight);
      
      // Export from the wrapper with explicit save
      await html2pdf()
        .set({
          ...pdfOptions,
          html2canvas: {
            ...pdfOptions.html2canvas,
            scrollY: 0,
            scrollX: 0,
            windowWidth: exportWrapper.scrollWidth || 794,
            windowHeight: exportWrapper.scrollHeight || 1123,
          }
        })
        .from(exportWrapper)
        .toPdf()
        .get('pdf')
        .then(function (pdf) {
          pdf.save(filename);
        });
      
      // Cleanup
      document.body.removeChild(exportWrapper);
      
      console.log('✅ PDF exported successfully (direct with styles):', filename);
      return;
    } catch (directError) {
      console.warn('⚠️ Direct with styles export failed:', directError.message);
    }

    // Find the actual resume content (skip wrapper divs)
    let contentElement = element;
    
    // Check if body only has a wrapper div
    if (element.children.length === 1) {
      const wrapper = element.children[0];
      console.log('🔍 Found wrapper:', wrapper.className);
      
      // If wrapper has children, use the wrapper's children container
      if (wrapper.children.length > 0) {
        // Look for the actual content container (usually .container or similar)
        const container = wrapper.querySelector('.container, .resume-container, [class*="container"]');
        if (container) {
          console.log('✅ Using container element for export');
          contentElement = container;
        } else {
          console.log('✅ Using wrapper element for export');
          contentElement = wrapper;
        }
      }
    }
    
    console.log('🔍 Export element children count:', contentElement.children.length);
    console.log('🔍 Export element text length:', (contentElement.innerText || contentElement.textContent || '').length);
    
    // Create a hidden container in parent document to host cloned content with styles
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'fixed';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = '210mm'; // A4 width
    tempContainer.style.minHeight = '297mm'; // A4 height
    tempContainer.style.background = 'white';
    tempContainer.style.padding = '20px';
    tempContainer.style.overflow = 'visible';
    document.body.appendChild(tempContainer);

    // Copy styles from iframe head (style and link rel="stylesheet")
    const head = iframeDoc.head;
    console.log('🔍 Iframe head exists:', !!head);
    
    if (head) {
      const styleEls = head.querySelectorAll('style');
      console.log('🔍 Found', styleEls.length, 'style elements');
      styleEls.forEach(s => {
        const cs = document.createElement('style');
        cs.textContent = s.textContent;
        tempContainer.appendChild(cs);
      });
      const linkEls = head.querySelectorAll('link[rel="stylesheet"]');
      console.log('🔍 Found', linkEls.length, 'stylesheet links');
      linkEls.forEach(l => {
        const cl = document.createElement('link');
        cl.rel = 'stylesheet';
        cl.href = l.href;
        tempContainer.appendChild(cl);
      });
    }

    // Clone the content element into container
    const clonedBody = contentElement.cloneNode(true);
    console.log('🔍 Cloned body innerHTML length:', clonedBody.innerHTML?.length || 0);
    tempContainer.appendChild(clonedBody);

    // Copy ALL inline styles from iframe elements to cloned elements
    const copyComputedStyles = (source, target) => {
      if (!source || !target) return;
      
      const sourceElements = source.getElementsByTagName('*');
      const targetElements = target.getElementsByTagName('*');
      
      for (let i = 0; i < Math.min(sourceElements.length, targetElements.length); i++) {
        const computedStyle = window.getComputedStyle(sourceElements[i]);
        const targetElement = targetElements[i];
        
        // Copy critical styles
        const criticalProps = [
          'display', 'position', 'width', 'height', 'margin', 'padding',
          'font-family', 'font-size', 'font-weight', 'color', 'background-color',
          'border', 'text-align', 'line-height', 'visibility', 'opacity'
        ];
        
        criticalProps.forEach(prop => {
          const value = computedStyle.getPropertyValue(prop);
          if (value) {
            targetElement.style[prop] = value;
          }
        });
      }
    };
    
    console.log('📋 Copying computed styles...');
    copyComputedStyles(contentElement, clonedBody);

    // Apply print-friendly styles
    applyPrintStyles(clonedBody);
    
    // DEBUG: Log what we're about to export
    console.log('📊 Final export element stats:');
    console.log('  - HTML length:', tempContainer.innerHTML.length);
    console.log('  - Text length:', (tempContainer.innerText || tempContainer.textContent || '').length);
    console.log('  - Children:', tempContainer.children.length);
    console.log('  - Dimensions:', tempContainer.offsetWidth, 'x', tempContainer.offsetHeight);
    
    // Wait a moment for styles to apply
    await new Promise(resolve => setTimeout(resolve, 100));

    // Generate PDF
    console.log('⏳ Generating PDF...');
    await html2pdf().set(pdfOptions).from(tempContainer).save();
    
    // Cleanup
    document.body.removeChild(tempContainer);
    
    console.log('✅ PDF exported successfully:', filename);
  } catch (error) {
    console.error('❌ Error exporting PDF:', error);
    throw new Error(`PDF export failed: ${error.message}`);
  }
};

/**
 * Export HTML element to PDF
 * @param {HTMLElement} element - HTML element to export
 * @param {string} filename - Name for the PDF file
 * @param {object} options - Additional options for PDF generation
 * @returns {Promise<void>}
 */
export const exportElementToPDF = async (element, filename = 'resume.pdf', options = {}) => {
  try {
    if (!element) {
      throw new Error('Invalid element reference');
    }

    console.log('📄 Starting PDF export from element...');

    // Default PDF options
    const defaultOptions = {
      margin: [10, 10, 10, 10],
      filename: filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait'
      }
    };

    // Merge with custom options
    const pdfOptions = { ...defaultOptions, ...options };

    // Clone the element
    const clonedElement = element.cloneNode(true);

    // Apply print-friendly styles
    applyPrintStyles(clonedElement);

    // Generate PDF
    console.log('⏳ Generating PDF...');
    await html2pdf().set(pdfOptions).from(clonedElement).save();
    
    console.log('✅ PDF exported successfully:', filename);
  } catch (error) {
    console.error('❌ Error exporting PDF:', error);
    throw new Error(`PDF export failed: ${error.message}`);
  }
};

/**
 * Apply print-friendly styles to element
 * @param {HTMLElement} element - Element to style
 */
const applyPrintStyles = (element) => {
  // Remove interactive elements
  const removeSelectors = ['button', 'input[type="button"]', '.no-print'];
  removeSelectors.forEach(selector => {
    const elements = element.querySelectorAll(selector);
    elements.forEach(el => el.remove());
  });

  // Ensure proper text rendering
  const textElements = element.querySelectorAll('*');
  textElements.forEach(el => {
    if (el.style) {
      // Prevent text from being cut off
      el.style.textRendering = 'optimizeLegibility';
      el.style.webkitFontSmoothing = 'antialiased';
    }
  });

  // Ensure images load properly
  const images = element.querySelectorAll('img');
  images.forEach(img => {
    if (img.src && !img.complete) {
      console.warn('⚠️ Image not fully loaded:', img.src);
    }
  });
};

/**
 * Generate a filename for the resume PDF
 * Format: FullName_ProfessionalTitle.pdf
 * @param {object} resumeData - Resume data containing personal info
 * @param {string} templateName - Name of the template
 * @returns {string} Generated filename
 */
export const generateFilename = (resumeData, templateName = 'Resume') => {
  try {
    const personalInfo = resumeData?.personalInfo || {};
    const name = resumeData?.fullName || personalInfo.name || 'Unknown';
    const title = resumeData?.title || personalInfo.title || '';
    
    // Sanitize name for filename
    const sanitizedName = name
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 50); // Limit length
    
    // Sanitize title for filename
    const sanitizedTitle = title
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50);
    
    // Generate filename: FullName_ProfessionalTitle.pdf or FullName_Resume.pdf if no title
    const filename = sanitizedTitle
      ? `${sanitizedName}_${sanitizedTitle}.pdf`
      : `${sanitizedName}_Resume.pdf`;
    
    return filename;
  } catch (error) {
    console.error('❌ Error generating filename:', error);
    return `Resume_${Date.now()}.pdf`;
  }
};

/**
 * Get PDF as blob instead of downloading
 * @param {HTMLElement} element - Element to convert to PDF
 * @param {object} options - PDF options
 * @returns {Promise<Blob>} PDF as blob
 */
export const getPDFBlob = async (element, options = {}) => {
  try {
    if (!element) {
      throw new Error('Invalid element reference');
    }

    const defaultOptions = {
      margin: [10, 10, 10, 10],
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2,
        useCORS: true,
        letterRendering: true
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait'
      }
    };

    const pdfOptions = { ...defaultOptions, ...options };
    const clonedElement = element.cloneNode(true);
    applyPrintStyles(clonedElement);

    // Generate PDF and get blob
    const pdf = await html2pdf().set(pdfOptions).from(clonedElement).outputPdf('blob');
    
    return pdf;
  } catch (error) {
    console.error('❌ Error generating PDF blob:', error);
    throw new Error(`PDF blob generation failed: ${error.message}`);
  }
};

/**
 * Preview PDF in a new window/tab
 * @param {HTMLElement} element - Element to convert to PDF
 * @param {object} options - PDF options
 * @returns {Promise<void>}
 */
export const previewPDF = async (element, options = {}) => {
  try {
    const blob = await getPDFBlob(element, options);
    const url = URL.createObjectURL(blob);
    
    // Open in new window
    const previewWindow = window.open(url, '_blank');
    
    if (!previewWindow) {
      throw new Error('Popup blocked. Please allow popups for this site.');
    }
    
    // Clean up the blob URL after a delay
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 60000); // 1 minute
    
    console.log('✅ PDF preview opened in new window');
  } catch (error) {
    console.error('❌ Error previewing PDF:', error);
    throw new Error(`PDF preview failed: ${error.message}`);
  }
};

/**
 * Export iframe content to a Word-compatible .doc file by saving HTML as .doc
 * Most versions of MS Word can open HTML saved with a .doc extension.
 * @param {HTMLIFrameElement} iframeRef
 * @param {string} filenameBase
 */
export const exportIframeToDOC = (iframeRef, filenameBase = 'resume') => {
  if (!iframeRef || !iframeRef.contentDocument) {
    throw new Error('Invalid iframe reference');
  }
  const doc = iframeRef.contentDocument || iframeRef.contentWindow.document;
  const html = `<!DOCTYPE html>${doc.documentElement.outerHTML}`;
  const blob = new Blob([html], { type: 'application/msword' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filenameBase}.doc`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
